/**
 * Route wrapper for the builder.
 *
 * Creation and editing share this shape and the same form: an edit is the same questionnaire with
 * a slug already decided. Being a route rather than a modal is what lets the header name it and
 * the browser's back button behave.
 *
 * @module Pages/BUs/Surveys
 */

import { UTLoading } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SurveyBuilder } from './Components/SurveyBuilder/SurveyBuilder';
import { useSurvey } from './Hooks/useSurvey';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

export const SurveyBuilderPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { surveyId } = useParams<{ surveyId: string }>();

  const { data: survey, isLoading } = useSurvey(surveyId);

  // The builder seeds from the survey, so it must not mount before it arrives.
  if (surveyId && isLoading) {
    return (
      <MainContent id='survey-builder-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <SurveyBuilder
      survey={survey}
      onDone={() => {
        navigate('/surveys');
      }}
      onCreated={id => {
        // The creation route becomes the edit route, so the next save patches instead of
        // creating a second survey with the same slug.
        navigate(`/surveys/${id}`, { replace: true });
      }}
    />
  );
};
